<template>
  <div class="modal fade" id="batch_upload_modal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="batch_upload_label" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content bg-white rounded-3">
        <div class="modal-header border-bottom-0">
          <h5 class="modal-title text-s fw-bold" id="batch_upload_label">{{ modal_title }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="batch_processing"></button>
        </div>
        <div class="modal-body overflow-hidden">
          <!-- Batch Type Indicator -->
          <div class="mb-4">
            <label class="form-label text-xs fw-bold d-block">Batch Type</label>
            <div class="alert alert-secondary text-xs d-flex align-items-center mb-0 py-2">
              <i class="fas fa-layer-group me-2 text-muted"></i>
              <span>{{ batch_type === 'company' ? 'Company Onboarding' : 'Individual Verification' }}</span>
            </div>
          </div>

          <!-- Upload Section -->
          <div v-if="!batch_job_id" class="upload-section">
            <div class="mb-4">
              <label class="form-label text-xs fw-bold">{{ upload_label }}</label>
              <div class="border-2 border-dashed border-gray-300 rounded-3 p-4 text-center bg-light">
                <input type="file" ref="batchFileInput" @change="handle_file_upload" accept=".csv,.xlsx,.xls" class="d-none" />
                <div v-if="!batch_file" class="py-3">
                  <i class="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
                  <p class="text-xs text-muted mb-2">Drag and drop your Excel/CSV file here or click to browse</p>
                  <p class="text-xxs text-muted mb-3">{{ file_format_hint }}</p>
                  <button type="button" class="btn btn-primary btn-sm rounded-3 text-xs px-4" @click="$refs.batchFileInput && $refs.batchFileInput.click()">
                    <i class="fas fa-upload me-2"></i> Select File
                  </button>
                </div>
                <div v-else class="py-3">
                  <div class="d-flex align-items-center justify-content-center mb-3">
                    <i class="fas fa-file-excel fa-2x text-success me-3"></i>
                    <div class="text-start">
                      <p class="text-xs fw-bold mb-1 text-truncate batch-width">{{ batch_file.name }}</p>
                      <p class="text-xxs text-muted mb-0">Size: {{ format_file_size(batch_file.size) }}</p>
                    </div>
                  </div>
                  <div class="d-flex justify-content-center gap-2">
                    <button type="button" class="btn btn-outline-danger btn-sm rounded-3 text-xs" @click="remove_file">
                      <i class="fas fa-times me-1"></i> Remove
                    </button>
                    <button type="button" class="btn btn-outline-primary btn-sm rounded-3 text-xs" @click="$refs.batchFileInput && $refs.batchFileInput.click()">
                      <i class="fas fa-sync me-1"></i> Change File
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Column Mapping -->
            <div v-if="batch_file" class="mb-4">
              <div class="alert alert-info text-xs d-flex align-items-center">
                <i class="fas fa-info-circle me-2"></i>
                Map your file columns to the required fields
              </div>
              
              <!-- Company Fields -->
              <div v-if="batch_type === 'company'" class="row g-3">
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label text-xs fw-bold">Company Name Column</label>
                    <select v-model="company_name_column" class="form-select text-xs rounded shadow-sm" @change="extract_records" :disabled="true">
                      <option value="">Select Column</option>
                      <option v-for="column in detected_columns" :key="column" :value="column">{{ column }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group">
                    <label class="form-label text-xs fw-bold">Registration Number Column</label>
                    <select v-model="registration_number_column" class="form-select text-xs rounded shadow-sm" @change="extract_records" :disabled="true">
                      <option value="">Select Column</option>
                      <option v-for="column in detected_columns" :key="column" :value="column">{{ column }}</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <!-- Individual Fields -->
              <div v-if="batch_type === 'individual'" class="row g-3">
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label text-xs fw-bold">ID Number Column</label>
                    <select v-model="id_number_column" class="form-select text-xs rounded shadow-sm" @change="extract_records" :disabled="true">
                      <option value="">Select Column</option>
                      <option v-for="column in detected_columns" :key="column" :value="column">{{ column }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label text-xs fw-bold">First Name Column</label>
                    <select v-model="first_name_column" class="form-select text-xs rounded shadow-sm" @change="extract_records" :disabled="true">
                      <option value="">Select Column</option>
                      <option v-for="column in detected_columns" :key="column" :value="column">{{ column }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-group">
                    <label class="form-label text-xs fw-bold">Last Name Column</label>
                    <select v-model="last_name_column" class="form-select text-xs rounded shadow-sm" @change="extract_records" :disabled="true">
                      <option value="">Select Column</option>
                      <option v-for="column in detected_columns" :key="column" :value="column">{{ column }}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Records Preview -->
            <div v-if="extracted_records.length > 0" class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="text-xs fw-bold mb-0">Preview {{ batch_type === 'company' ? 'Companies' : 'Individuals' }}</h6>
                <div>
                  <span class="badge bg-primary text-xs me-2">{{ extracted_records.length }} total</span>
                  <span class="badge bg-success text-xs me-2">{{ valid_records_count }} valid</span>
                  <span v-if="invalid_records_count > 0" class="badge bg-danger text-xs me-2">{{ invalid_records_count }} invalid format</span>

                </div>
              </div>
              <div class="border rounded overflow-auto border-height">
                <table class="table table-sm table-hover mb-0" id="records-table">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th class="text-xs">#</th>
                      <th v-if="batch_type === 'company'" class="text-xs">Company Name</th>
                      <th v-if="batch_type === 'company'" class="text-xs">Registration Number</th>
                      <th v-if="batch_type === 'individual'" class="text-xs">ID Number</th>
                      <th v-if="batch_type === 'individual'" class="text-xs">First Name</th>
                      <th v-if="batch_type === 'individual'" class="text-xs">Last Name</th>
                      <th class="text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Company Records -->
                    <template v-if="batch_type === 'company'">
                      <tr v-for="(record, index) in extracted_records" :key="index">
                        <td class="text-xs">{{ index + 1 }}</td>
                        <td class="text-xs">
                          <span :class="{'text-danger': !record.name}">{{ record.name || 'Missing' }}</span>
                        </td>
                        <td class="text-xs">
                          <div :class="{'text-danger': !record.registration_number || (record._validation && !record._validation.registration_valid)}">
                            {{ record.registration_number || 'Missing' }}
                          </div>
                          <div v-if="record._validation && record._validation.validation_error" class="text-xxs text-danger mt-1">
                            <i class="fas fa-exclamation-triangle me-1"></i>
                            {{ record._validation.validation_error }}
                          </div>
                        </td>
                        <td class="text-xs">
                          <span v-if="record.name && record.registration_number && record._validation && record._validation.registration_valid" class="badge bg-success-light text-success">
                            <i class="fas fa-check-circle me-1"></i> Valid
                          </span>
                          <span v-else-if="record._validation && !record._validation.registration_valid && record.registration_number" class="badge bg-danger-light text-danger">
                            <i class="fas fa-times-circle me-1"></i> Invalid Format
                          </span>
                          <span v-else class="badge bg-warning-light text-warning">
                            <i class="fas fa-exclamation-circle me-1"></i> Incomplete
                          </span>
                        </td>
                      </tr>
                    </template>
                    
                    <!-- Individual Records -->
                    <template v-if="batch_type === 'individual'">
                      <tr v-for="(record, index) in extracted_records" :key="index">
                        <td class="text-xs">{{ index + 1 }}</td>
                        <td class="text-xs">
                          <div :class="{'text-danger': !record.id_number || (record._validation && !record._validation.id_valid)}">
                            {{ record.id_number || 'Missing' }}
                          </div>
                          <div v-if="record._validation && record._validation.id_error" class="text-xxs text-danger mt-1">
                            <i class="fas fa-exclamation-triangle me-1"></i>
                            {{ record._validation.id_error }}
                          </div>
                        </td>
                        <td class="text-xs">
                          <span :class="{'text-danger': !record.first_name || (record._validation && !record._validation.first_name_valid)}">{{ record.first_name || 'Missing' }}</span>
                        </td>
                        <td class="text-xs">
                          <span :class="{'text-danger': !record.last_name || (record._validation && !record._validation.last_name_valid)}">{{ record.last_name || 'Missing' }}</span>
                        </td>
                        <td class="text-xs">
                          <span v-if="record.id_number && record.first_name && record.last_name && record._validation && record._validation.all_valid" class="badge bg-success-light text-success">
                            <i class="fas fa-check-circle me-1"></i> Valid
                          </span>
                          <span v-else-if="record._validation && (!record._validation.id_valid || !record._validation.first_name_valid || !record._validation.last_name_valid)" class="badge bg-danger-light text-danger">
                            <i class="fas fa-times-circle me-1"></i> Invalid
                          </span>
                          <span v-else class="badge bg-warning-light text-warning">
                            <i class="fas fa-exclamation-circle me-1"></i> Incomplete
                          </span>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Processing Section -->
          <div v-else class="processing-section">
            <!-- Connection Status Alert -->
            <div v-if="connection_error" class="alert alert-warning text-xs d-flex align-items-start mb-3">
              <i class="fas fa-wifi me-2 mt-1"></i>
              <div class="flex-grow-1">
                <strong>Connection Issue Detected</strong><br>
                Unable to connect to server. Status updates may be delayed.
                <button class="btn btn-outline-warning btn-sm ms-2 text-xxs" @click="manual_refresh" :disabled="fetching_status">
                  <i class="fas fa-sync-alt me-1" :class="{'fa-spin': fetching_status}"></i>
                  Try Reconnect
                </button>
              </div>
            </div>

            <!-- API Errors Display -->
            <div v-if="api_errors.length > 0" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="text-xs fw-bold mb-0 text-warning">
                  <i class="fas fa-exclamation-triangle me-1"></i>
                  Recent Messages ({{ api_errors.length }})
                </h6>
                <button class="btn btn-outline-secondary btn-sm text-xxs" @click="clear_all_errors" title="Clear all messages">
                  <i class="fas fa-times me-1"></i> Clear All
                </button>
              </div>
              <div class="border rounded p-2 bg-light" style="max-height: 150px; overflow-y: auto;">
                <div v-for="error in api_errors" :key="error.id" 
                    :class="['alert', 'alert-' + (error.type === 'success' ? 'success' : error.type === 'info' ? 'info' : error.type === 'warning' ? 'warning' : 'danger'), 'p-2', 'mb-2', 'text-xxs']">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="fw-bold">{{ error.message }}</div>
                      <div v-if="error.details" class="text-muted mt-1">{{ error.details }}</div>
                      <small class="text-muted">{{ error.timestamp }}</small>
                    </div>
                    <button class="btn btn-sm p-0 ms-2" @click="clear_api_error(error.id)" title="Dismiss">
                      <i class="fas fa-times text-muted"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Processing Header -->
            <div class="processing-header bg-light rounded p-3 mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 class="text-xs fw-bold mb-1">
                    <i :class="batch_status_icon" class="me-2"></i>
                    Processing Batch {{ batch_type === 'company' ? 'Onboarding' : 'Verification' }}
                    <span v-if="polling_interval" class="badge bg-success ms-2">Live</span>
                    <span v-else-if="connection_error" class="badge bg-warning ms-2">Disconnected</span>
                    <span v-else class="badge bg-secondary ms-2">Paused</span>
                  </h6>
                  <p class="text-xxs text-muted mb-0">Workflow: <span class="fw-bold">{{ current_workflow_name }}</span></p>
                  <p class="text-xxs text-muted">
                    <i class="fas fa-sync me-1" :class="{'fa-spin': polling_interval && !connection_error}"></i>
                    {{ connection_error ? 'Connection issues detected' : polling_interval ? 'Auto-refreshing every 3 seconds' : 'Updates paused' }}
                  </p>
                </div>
                <div class="text-end">
                  <span :class="['badge', 'text-xs', 'mb-2', 'd-block', 'bg-' + batch_status_class]">{{ batch_status_text }}</span>
                  <div v-if="retryable_count > 0" class="text-xxs text-muted">
                    {{ retryable_count }} failed {{ batch_type === 'company' ? 'companies' : 'individuals' }} available for retry
                  </div>
                </div>
              </div>
              
              <!-- Progress Bar -->
              <div class="progress mb-2 progress-height">
                <div 
                  class="progress-bar progress-bar-striped" 
                  :class="{'progress-bar-animated': batch_status === 'processing'}"
                  role="progressbar"
                  :style="{ width: overall_progress + '%' }"
                  :aria-valuenow="overall_progress"
                  aria-valuemin="0"
                  aria-valuemax="100">
                </div>
              </div>
              
              <!-- Status Counters -->
              <div class="row g-2 text-center">
                <div class="col-3">
                  <div class="text-xxs text-muted">Total</div>
                  <div class="text-xs fw-bold">{{ total_records }}</div>
                </div>
                <div class="col-3">
                  <div class="text-xxs text-muted">Processing</div>
                  <div class="text-xs fw-bold text-info">{{ processing_count }}</div>
                </div>
                <div class="col-3">
                  <div class="text-xxs text-muted">Completed</div>
                  <div class="text-xs fw-bold text-success">{{ successful_count }}</div>
                </div>
                <div class="col-3">
                  <div class="text-xxs text-muted">Failed</div>
                  <div class="text-xs fw-bold text-danger">{{ failed_count }}</div>
                </div>
              </div>
            </div>

            <!-- Enhanced Records Processing Table -->
            <div class="records-processing">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="text-xs fw-bold mb-0">{{ batch_type === 'company' ? 'Company' : 'Individual' }} Progress Details</h6>
                <div class="d-flex align-items-center">
                  <button class="btn btn-outline-primary btn-sm text-xxs me-2" @click="manual_refresh" :disabled="fetching_status">
                    <i class="fas fa-sync-alt me-1" :class="{'fa-spin': fetching_status}"></i>
                    {{ fetching_status ? 'Refreshing...' : 'Refresh' }}
                  </button>
                  <small class="text-muted text-nowrap">
                    {{ connection_error ? 'Connection Error' : 'Updated: ' + (last_updated_time || 'Never') }}
                  </small>
                </div>
              </div>
              
              <!-- Records Table -->
              <div class="border rounded overflow-auto table-height">
                <table class="table table-sm table-hover mb-0">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th class="text-xs">#</th>
                      <th v-if="batch_type === 'company'" class="text-xs">Company</th>
                      <th v-if="batch_type === 'individual'" class="text-xs">Name</th>
                      <th v-if="batch_type === 'individual'" class="text-xs">ID Number</th>
                      <th class="text-xs">Status</th>
                      <th class="text-xs">Current Service</th>
                      <th class="text-xs">Service Progress</th>
                      <th class="text-xs">Error Details</th>
                      <th class="text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Company Records -->
                    <template v-if="batch_type === 'company'">
                      <tr v-for="(company, index) in batch_companies" :key="index">
                        <td class="text-xs">{{ index + 1 }}</td>
                        <td class="text-xs">
                          <div class="fw-bold text-truncate company-width">{{ company.company_name }}</div>
                          <div class="text-xxs text-muted">{{ company.registration_number }}</div>
                        </td>
                        <td class="text-xs">
                          <span :class="['badge', 'bg-' + getStatusClass(company.status)]">
                            <i :class="getStatusIcon(company.status)" class="me-1"></i>
                            {{ company.status }}
                          </span>
                          <div v-if="company.status === 'processing' && company.current_service" class="text-xxs text-muted mt-1">
                            {{ company.current_service }}
                          </div>
                        </td>
                        <td class="text-xs">
                          <div v-if="company.current_service">
                            <span class="badge bg-info text-xxs">{{ company.current_service }}</span>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-xs">
                          <div v-if="company.total_services > 0" class="d-flex align-items-center">
                            <div class="progress me-2 progress-width">
                              <div 
                                class="progress-bar bg-info" 
                                role="progressbar" 
                                :style="{ width: company.progress_percentage + '%' }"
                                :aria-valuenow="company.progress_percentage"
                                aria-valuemin="0" 
                                aria-valuemax="100">
                              </div>
                            </div>
                            <small class="text-muted">{{ company.completed_services }}/{{ company.total_services }}</small>
                          </div>
                          <div v-else-if="company.status === 'processing'" class="d-flex align-items-center">
                            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                            <span class="text-xxs">Starting...</span>
                          </div>
                          <span v-else-if="company.status === 'completed'" class="text-success text-xxs">
                            <i class="fas fa-check-circle me-1"></i> Complete
                          </span>
                          <span v-else-if="company.status === 'failed'" class="text-danger text-xxs">
                            <i class="fas fa-times-circle me-1"></i> Failed
                          </span>
                          <span v-else class="text-muted text-xxs">-</span>
                        </td>
                        <td class="text-xs">
                          <div v-if="company.error_message" class="text-danger">
                            <div class="d-flex align-items-start">
                              <i class="fas fa-exclamation-triangle me-1 mt-1 flex-shrink-0"></i>
                              <div class="flex-grow-1">
                                <div class="text-truncate error-width" :title="get_detailed_error_message(company)">
                                  {{ company.error_message }}
                                </div>
                                <div v-if="company.failed_service" class="text-xxs text-muted mt-1">
                                  Failed at: {{ company.failed_service }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-xs">
                          <div v-if="company.status === 'failed'" class="d-flex flex-column gap-1">
                            <button
                              v-if="!are_retry_options_visible(index)"
                              class="btn btn-warning btn-sm text-xxs px-2 py-1"
                              @click="show_retry_options(index)"
                              :disabled="retrying_company === index"
                              title="Show retry options">
                              <i class="fas fa-redo me-1"></i>
                              Retry
                            </button>
                            
                            <div v-if="are_retry_options_visible(index)" class="d-flex flex-column gap-1">
                              <button
                                class="btn btn-warning btn-sm text-xxs px-2 py-1"
                                @click="retry_company(company, index, false)"
                                :disabled="retrying_company === index"
                                :title="`Continue from ${company.failed_service || 'failed service'}`">
                                <i class="fas fa-play me-1" :class="{'fa-spin': retrying_company === index}"></i>
                                {{ retrying_company === index ? 'Retrying...' : (company.failed_service ? `Continue from ${company.failed_service}` : 'Continue') }}
                              </button>
                              
                              <button
                                class="btn btn-outline-secondary btn-sm text-xxs px-1 py-1"
                                @click="hide_retry_options(index)"
                                title="Hide retry options">
                                <i class="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                          <div v-else-if="company.status === 'processing'" class="d-flex align-items-center">
                            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                            <span class="text-muted text-xxs">Processing...</span>
                          </div>
                          <div v-else-if="company.status === 'completed'" class="text-success text-xxs">
                            <i class="fas fa-check-circle me-1"></i>
                            Complete
                          </div>
                          <span v-else class="text-muted text-xxs">-</span>
                        </td>
                      </tr>
                    </template>

                    <!-- Individual Records -->
                    <template v-if="batch_type === 'individual'">
                      <tr v-for="(individual, index) in batch_individuals" :key="index">
                        <td class="text-xs">{{ index + 1 }}</td>
                        <td class="text-xs">
                          <div class="fw-bold text-truncate company-width">{{ individual.first_name }} {{ individual.last_name }}</div>
                        </td>
                        <td class="text-xs">
                          <div class="text-xxs text-muted">{{ individual.id_number }}</div>
                        </td>
                        <td class="text-xs">
                          <span :class="['badge', 'bg-' + getStatusClass(individual.status)]">
                            <i :class="getStatusIcon(individual.status)" class="me-1"></i>
                            {{ individual.status }}
                          </span>
                          <div v-if="individual.status === 'processing' && individual.current_service" class="text-xxs text-muted mt-1">
                            {{ individual.current_service }}
                          </div>
                        </td>
                        <td class="text-xs">
                          <div v-if="individual.current_service">
                            <span class="badge bg-info text-xxs">{{ individual.current_service }}</span>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-xs">
                          <div v-if="individual.total_services > 0" class="d-flex align-items-center">
                            <div class="progress me-2 progress-width">
                              <div 
                                class="progress-bar bg-info" 
                                role="progressbar" 
                                :style="{ width: individual.progress_percentage + '%' }"
                                :aria-valuenow="individual.progress_percentage"
                                aria-valuemin="0" 
                                aria-valuemax="100">
                              </div>
                            </div>
                            <small class="text-muted">{{ individual.completed_services }}/{{ individual.total_services }}</small>
                          </div>
                          <div v-else-if="individual.status === 'processing'" class="d-flex align-items-center">
                            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                            <span class="text-xxs">Starting...</span>
                          </div>
                          <span v-else-if="individual.status === 'completed'" class="text-success text-xxs">
                            <i class="fas fa-check-circle me-1"></i> Complete
                          </span>
                          <span v-else-if="individual.status === 'failed'" class="text-danger text-xxs">
                            <i class="fas fa-times-circle me-1"></i> Failed
                          </span>
                          <span v-else class="text-muted text-xxs">-</span>
                        </td>
                        <td class="text-xs">
                          <div v-if="individual.error_message" :class="individual.status === 'completed' ? 'text-info' : 'text-danger'">
                            <div class="d-flex align-items-start">
                              <i :class="individual.status === 'completed' ? 'fas fa-info-circle' : 'fas fa-exclamation-triangle'" class="me-1 mt-1 flex-shrink-0"></i>
                              <div class="flex-grow-1">
                                <div class="text-truncate error-width" :title="get_detailed_error_message(individual)">
                                  {{ individual.error_message }}
                                </div>
                                <div v-if="individual.failed_service" class="text-xxs text-muted mt-1">
                                  Failed at: {{ individual.failed_service }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <span v-else class="text-muted">-</span>
                        </td>
                        <td class="text-xs">
                          <div v-if="individual.status === 'failed'" class="d-flex flex-column gap-1">
                            <button
                              v-if="!are_retry_options_visible(index)"
                              class="btn btn-warning btn-sm text-xxs px-2 py-1"
                              @click="show_retry_options(index)"
                              :disabled="retrying_individual === index"
                              title="Show retry options">
                              <i class="fas fa-redo me-1"></i>
                              Retry
                            </button>
                            
                            <div v-if="are_retry_options_visible(index)" class="d-flex flex-column gap-1">
                              <button
                                class="btn btn-warning btn-sm text-xxs px-2 py-1"
                                @click="retry_individual(individual, index, false)"
                                :disabled="retrying_individual === index"
                                :title="`Continue from ${individual.failed_service || 'failed service'}`">
                                <i class="fas fa-play me-1" :class="{'fa-spin': retrying_individual === index}"></i>
                                {{ retrying_individual === index ? 'Retrying...' : (individual.failed_service ? `Continue from ${individual.failed_service}` : 'Continue') }}
                              </button>
                              
                              <button
                                class="btn btn-outline-secondary btn-sm text-xxs px-1 py-1"
                                @click="hide_retry_options(index)"
                                title="Hide retry options">
                                <i class="fas fa-times"></i>
                              </button>
                            </div>
                          </div>
                          <div v-else-if="individual.status === 'processing'" class="d-flex align-items-center">
                            <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                            <span class="text-muted text-xxs">Processing...</span>
                          </div>
                          <div v-else-if="individual.status === 'completed'" class="text-success text-xxs">
                            <i class="fas fa-check-circle me-1"></i>
                            Complete
                          </div>
                          <span v-else class="text-muted text-xxs">-</span>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <!-- Messages -->
          <div class="mt-3">
            <!-- Main Error Display -->
            <div v-if="batch_error" class="alert alert-danger text-xs d-flex align-items-start">
              <i class="fas fa-exclamation-circle me-2 mt-1"></i>
              <div class="flex-grow-1">
                <div>{{ batch_error }}</div>
                <div v-if="last_error_timestamp" class="text-xxs mt-1 opacity-75">
                  Last error: {{ last_error_timestamp }}
                </div>
              </div>
              <button class="btn btn-sm p-0 ms-2" @click="batch_error = ''" title="Dismiss error">
                <i class="fas fa-times text-muted"></i>
              </button>
            </div>
            
            <!-- Success Message -->
            <div v-if="batch_success" class="alert alert-success text-xs d-flex align-items-start">
              <i class="fas fa-check-circle me-2 mt-1"></i>
              <div class="flex-grow-1">{{ batch_success }}</div>
              <button class="btn btn-sm p-0 ms-2" @click="batch_success = ''" title="Dismiss message">
                <i class="fas fa-times text-muted"></i>
              </button>
            </div>
            
            <!-- Retry Summary -->
            <div v-if="retryable_count > 0 && batch_status !== 'processing'" 
                class="alert alert-info text-xs d-flex align-items-start">
              <i class="fas fa-info-circle me-2 mt-1"></i>
              <div>
                <strong>Retry Available:</strong> {{ retryable_count }} {{ batch_type === 'company' ? 'companies' : 'individuals' }} failed and can be retried.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer border-top-0">
          <button 
            v-if="!batch_job_id" 
            type="button" 
            class="btn btn-outline-secondary btn-sm rounded-3 text-xs" 
            data-bs-dismiss="modal">
            Cancel
          </button>
          <button 
            v-if="!batch_job_id" 
            type="button" 
            class="btn btn-primary btn-sm rounded-3 text-xs fw-bold px-4"
            @click="process_batch_upload"
            :disabled="!can_start_processing || batch_processing">
            <i class="fas fa-play me-1"></i> 
            <span v-if="batch_processing">Starting...</span>
            <span v-else>Start {{ batch_type === 'company' ? 'Onboarding' : 'Verification' }} ({{ valid_records_count }} {{ batch_type === 'company' ? 'companies' : 'individuals' }})</span>
          </button>
          <button 
            v-else 
            type="button" 
            class="btn btn-outline-secondary btn-sm rounded-3 text-xs" 
            data-bs-dismiss="modal"
            :disabled="batch_status === 'processing'">
            <span v-if="batch_status === 'processing'">Processing...</span>
            <span v-else>Close</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { API_BASE_URL } from "@/config";
import { validate_id } from "@/utils/validations.js";
import * as XLSX from 'xlsx';

export default {
  name: 'BatchUpload',
  mixins: [MAIN_CONTROLLER],

  data() {
    return {
      batch_type: 'company', // 'company' or 'individual'
      dataTable_initialized: false,
      currentPage: 1,
      pageSize: 20,
      batch_file: null,
      detected_columns: [],
      // Company fields
      company_name_column: '',
      registration_number_column: '',
      // Individual fields
      id_number_column: '',
      first_name_column: '',
      last_name_column: '',
      has_header_row: true,
      extracted_records: [], // Unified for both companies and individuals
      batch_processing: false,
      batch_error: '',
      batch_success: '',
      current_batch_workflow_id: null,
      current_workflow_name: '',
      batch_job_id: null,
      batch_job_data: {},
      batch_companies: [],
      batch_individuals: [], // Add this new property
      polling_interval: null,
      fetching_status: false,
      last_updated_time: null,
      file_processing: false,
      retrying_company: null,
      skipping_company: null,
      api_errors: [],           // Array to store API error messages
      retry_options_visible: {}, // Track which companies show retry options
      error_display_timeout: null,
      connection_error: false,
      last_error_timestamp: null,
    };
  },

  computed: {
    modal_title() {
      return this.batch_type === 'company' ? 'Batch Company Onboarding' : 'Batch Individual Verification';
    },
    
    upload_label() {
      return this.batch_type === 'company' ? 'Upload Company Data' : 'Upload Individual Data';
    },
    
    file_format_hint() {
      return 'Supported formats: CSV, Excel (.xlsx, .xls)';
    },
    
    valid_records_count() {
      if (this.batch_type === 'company') {
        return this.extracted_records.filter(c => 
          (c.name || '').trim() &&
          (c.registration_number || '').trim() &&
          c._validation && c._validation.registration_valid === true
        ).length;
      } else {
        return this.extracted_records.filter(i => 
          (i.id_number || '').trim() &&
          (i.first_name || '').trim() &&
          (i.last_name || '').trim() &&
          i._validation && i._validation.all_valid === true
        ).length;
      }
    },

    invalid_records_count() {
      if (this.batch_type === 'company') {
        return this.extracted_records.filter(c =>
          (c.name || '').trim() &&
          (c.registration_number || '').trim() &&
          c._validation && c._validation.registration_valid === false
        ).length;
      } else {
        return this.extracted_records.filter(i =>
          (i.id_number || '').trim() &&
          (i.first_name || '').trim() &&
          (i.last_name || '').trim() &&
          i._validation && i._validation.all_valid === false
        ).length;
      }
    },
    
    can_start_processing() {
      if (this.batch_type === 'company') {
        return this.batch_file && 
               this.company_name_column && 
               this.registration_number_column && 
               this.valid_records_count > 0 &&
               true;
      } else {
        return this.batch_file && 
               this.id_number_column && 
               this.first_name_column &&
               this.last_name_column &&
               this.valid_records_count > 0;
      }
    },

    batch_status() {
      return this.batch_job_data.status || 'pending';
    },

    total_records() {
      return this.batch_type === 'company' 
        ? (this.batch_job_data.total_companies || 0)
        : (this.batch_job_data.total_individuals || 0);
    },
    
    processing_count() {
      if (this.batch_type === 'company') {
        return this.batch_companies.filter(c => 
          String(c.status || '').toUpperCase() === 'PROCESSING'
        ).length;
      } else {
        return this.batch_individuals.filter(i => 
          String(i.status || '').toUpperCase() === 'PROCESSING'
        ).length;
      }
    },

    successful_count() {
      return this.batch_type === 'company'
        ? (this.batch_job_data.successful_companies || 0)
        : (this.batch_job_data.successful_individuals || 0);
    },
    
    failed_count() {
      return this.batch_type === 'company'
        ? (this.batch_job_data.failed_companies || 0)
        : (this.batch_job_data.failed_individuals || 0);
    },

    retryable_count() {
      if (this.batch_type === 'company') {
        return this.batch_companies.filter(c => c.status === 'failed').length;
      } else {
        return this.batch_individuals.filter(i => i.status === 'failed').length;
      }
    },

    overall_progress() {
      if (this.total_records === 0) return 0;
      const processed = this.batch_type === 'company' 
        ? (this.batch_job_data.processed_companies || 0)
        : (this.batch_job_data.processed_individuals || 0);
      return Math.round((processed / this.total_records) * 100);
    },
    
    batch_status_text() {
      const statusMap = {
        'pending': 'Pending',
        'processing': 'Processing',
        'completed': 'Completed',
        'failed': 'Failed'
      };
      return statusMap[this.batch_status] || this.batch_status;
    },
    batch_status_class() {
      const classMap = {
        'pending': 'secondary',
        'processing': 'info',
        'completed': 'success',
        'failed': 'danger'
      };
      return classMap[this.batch_status] || 'secondary';
    },
    batch_status_icon() {
      const iconMap = {
        'pending': 'fas fa-clock',
        'processing': 'fas fa-spinner fa-spin',
        'completed': 'fas fa-check-circle',
        'failed': 'fas fa-exclamation-circle'
      };
      return iconMap[this.batch_status] || 'fas fa-clock';
    },

    has_active_errors() {
      return this.api_errors.length > 0 || this.batch_error || this.connection_error;
    },

    processing_companies_with_errors() {
      return this.batch_companies.filter(c => 
        c.status === 'failed' && c.error_message
      );
    },

    retryable_companies_count() {
      return this.batch_companies.filter(c => c.status === 'failed').length;
    },

    retryable_individuals_count() {
      return this.batch_individuals.filter(i => i.status === 'failed').length;
    },
    
    // ...existing computed properties
  },

  watch: {
    batch_type() {
      // Reset columns and extracted data when batch type changes
      this.company_name_column = '';
      this.registration_number_column = '';
      this.id_number_column = '';
      this.first_name_column = '';
      this.last_name_column = '';
      this.extracted_records = [];
      
      // Re-detect columns if file is already loaded
      if (this.batch_file) {
        this.auto_detect_columns();
        this.extract_records();
      }
    }
  },

  methods: {
    getStatusClass(status) {
      const s = (status || '').toString().toUpperCase();

      const classMap = {
        'PENDING': 'secondary',
        'PROCESSING': 'info',
        'COMPLETED': 'success',
        'FAILED': 'danger',
        'CANCELLED': 'warning'
      };
      return classMap[s] || 'secondary';
    },

    getStatusIcon(status) {
      const s = (status || '').toString().toUpperCase();
      const iconMap = {
        'PENDING': 'fas fa-clock',
        'PROCESSING': 'fas fa-spinner fa-spin',
        'COMPLETED': 'fas fa-check',
        'FAILED': 'fas fa-times',
        'CANCELLED': 'fas fa-ban'
      };
      return iconMap[s] || 'fas fa-clock';
    },

    async retry_company(company, index, retryFromBeginning = false) {
      if (!company || !this.batch_job_id) return;

      // Prevent multiple simultaneous retries
      if (this.retrying_company !== null) {
        this.show_error('Another retry is in progress. Please wait.');
        return;
      }

      this.retrying_company = index;
      this.clear_all_errors();

      const TOKEN = localStorage.getItem('token');
      const API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_company_retry/`;

      // IMPORTANT: Use the company's exact data from the batch_companies array
      const payload = {
        batch_job_id: this.batch_job_id,
        company_name: company.company_name,  // Use exact company_name from company object
        registration_number: company.registration_number,  // Use exact registration_number
        retry_from_beginning: retryFromBeginning
      };

      try {
        this.add_api_error({
          message: `Starting retry for ${company.company_name}...`,
          type: 'info'
        });

        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          },
          body: JSON.stringify(payload)
        });

        let result;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          const textResponse = await response.text();
          console.error('[FRONTEND RETRY] Non-JSON response:', textResponse);
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }

        if (response.ok && result.status === 'success') {
          const retryType = retryFromBeginning ? 'from beginning' : 'from failed service';
          this.show_success(`Successfully started retry for ${company.company_name} ${retryType}`);
          
          // Update UI immediately for THIS specific company
          const companyIndex = this.batch_companies.findIndex(
            c => c.company_name === company.company_name && 
                c.registration_number === company.registration_number
          );
          
          if (companyIndex !== -1) {
            // Create a new object to trigger Vue reactivity
            const updatedCompany = {
              ...this.batch_companies[companyIndex],
              status: 'processing',
              error_message: null,
              failed_service: null,
              current_service: 'Starting retry...'
            };
            
            if (retryFromBeginning) {
              updatedCompany.completed_services = 0;
              updatedCompany.progress_percentage = 0;
              updatedCompany.failed_service_index = null;
            }
            
            // Use Vue.set or array splice to ensure reactivity
            this.batch_companies[companyIndex] = updatedCompany;
          }

          // Force batch status back to processing
          this.batch_job_data.status = 'processing';
          
          // Restart polling with immediate refresh
          this.restart_polling();
          
          // Hide retry options after successful retry
          this.hide_retry_options(index);
          
          // Force immediate status refresh after short delay
          setTimeout(() => {
            this.fetch_batch_job_status();
          }, 1000);

        } else {
          // Handle API error response
          const errorMessage = result.message || `HTTP ${response.status}: ${response.statusText}`;
          this.add_api_error({
            message: `Retry failed for ${company.company_name}`,
            details: errorMessage,
            type: 'error'
          });
          this.show_error(errorMessage);
        }
      } catch (error) {
        console.error('[FRONTEND RETRY] Error:', error);
        
        const errorMessage = this.format_error_message(error);
        this.add_api_error({
          message: `Network error during retry for ${company.company_name}`,
          details: errorMessage,
          type: 'error'
        });
        this.show_error(`Failed to retry company: ${errorMessage}`);
        
        // Mark connection error if it's a network issue
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
          this.connection_error = true;
        }
      } finally {
        this.retrying_company = null;
      }
    },

    async retry_individual(individual, index, retryFromBeginning = false) {
      if (!individual || !this.batch_job_id) return;

      if (this.retrying_individual !== null) {
        this.show_error('Another retry is in progress. Please wait.');
        return;
      }

      this.retrying_individual = index;
      this.clear_all_errors();

      const TOKEN = localStorage.getItem('token');
      const API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_individual_retry/`;

      const payload = {
        batch_job_id: this.batch_job_id,
        first_name: individual.first_name,
        last_name: individual.last_name,
        id_number: individual.id_number,
        retry_from_beginning: retryFromBeginning
      };

      try {
        this.add_api_error({
          message: `Starting retry for ${individual.first_name} ${individual.last_name}...`,
          type: 'info'
        });

        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          },
          body: JSON.stringify(payload)
        });

        let result;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          const textResponse = await response.text();
          console.error('[FRONTEND RETRY] Non-JSON response:', textResponse);
          throw new Error(`Server returned ${response.status}: ${response.statusText}`);
        }

        if (response.ok && result.status === 'success') {
          const retryType = retryFromBeginning ? 'from beginning' : 'from failed service';
          this.show_success(`Successfully started retry for ${individual.first_name} ${individual.last_name} ${retryType}`);
          
          const individualIndex = this.batch_individuals.findIndex(
            i => i.first_name === individual.first_name && 
                i.last_name === individual.last_name &&
                i.id_number === individual.id_number
          );
          
          if (individualIndex !== -1) {
            const updatedIndividual = {
              ...this.batch_individuals[individualIndex],
              status: 'processing',
              error_message: null,
              failed_service: null,
              current_service: 'Starting retry...'
            };
            
            if (retryFromBeginning) {
              updatedIndividual.completed_services = 0;
              updatedIndividual.progress_percentage = 0;
              updatedIndividual.failed_service_index = null;
            }
            
            this.batch_individuals[individualIndex] = updatedIndividual;
          }

          this.batch_job_data.status = 'processing';
          this.restart_polling();
          this.hide_retry_options(index);
          
          setTimeout(() => {
            this.fetch_batch_job_status();
          }, 1000);

        } else {
          const errorMessage = result.message || `HTTP ${response.status}: ${response.statusText}`;
          this.add_api_error({
            message: `Retry failed for ${individual.first_name} ${individual.last_name}`,
            details: errorMessage,
            type: 'error'
          });
          this.show_error(errorMessage);
        }
      } catch (error) {
        console.error('[FRONTEND RETRY] Error:', error);
        
        const errorMessage = this.format_error_message(error);
        this.add_api_error({
          message: `Network error during retry for ${individual.first_name} ${individual.last_name}`,
          details: errorMessage,
          type: 'error'
        });
        this.show_error(`Failed to retry individual: ${errorMessage}`);
        
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
          this.connection_error = true;
        }
      } finally {
        this.retrying_individual = null;
      }
    },


    restart_polling() {
      
      if (this.polling_interval) {
        clearInterval(this.polling_interval);
        this.polling_interval = null;
      }
      
      this.start_status_polling();
    },

    stop_polling() {
      if (this.polling_interval) {
        clearInterval(this.polling_interval);
        this.polling_interval = null;
      }
    },

    normalize_registration_number(regNumber) {
      if (!regNumber) return regNumber;

      let normalized = String(regNumber).trim();
      normalized = normalized.replace(/\s+/g, '');

      normalized = normalized.replace(/[-_]/g, '/');

      if (/^\d{12}$/.test(normalized)) {
        normalized = normalized.substring(0, 4) + '/' +
                    normalized.substring(4, 10) + '/' +
                    normalized.substring(10, 12);
      }
      return normalized;
    },

    open_batch_upload_modal(workflow_id, workflow_name, batchType = 'company') {
      this.current_batch_workflow_id = workflow_id;
      this.current_workflow_name = workflow_name;
      this.batch_type = batchType; // Set the batch type before reset
      this.reset_batch_upload_state();

      const el = document.getElementById('batch_upload_modal');
      const modalAPI = window.bootstrap && window.bootstrap.Modal;
      if (modalAPI && typeof modalAPI.getOrCreateInstance === 'function') {
        const modalInstance = modalAPI.getOrCreateInstance(el);
        modalInstance.show();
        return;
      }

      if (window.$ && typeof window.$ === 'function' && window.$.fn && window.$.fn.modal) {
        window.$('#batch_upload_modal').modal('show');
        return;
      }
    },

    reset_batch_upload_state() {
      // Stop polling first
      this.stop_polling();
      
      // Clear all errors
      this.clear_all_errors();
      
      // Store the current batch type to preserve it
      const currentBatchType = this.batch_type;
      
      // Reset all state
      this.reset_file_context();
      this.has_header_row = true;
      this.batch_processing = false;
      this.batch_error = '';
      this.batch_success = '';
      this.batch_job_id = null;
      this.batch_job_data = {};
      this.batch_companies = [];
      this.batch_individuals = [];
      this.fetching_status = false;
      this.last_updated_time = null;
      this.file_processing = false;
      this.retrying_company = null;
      this.retrying_individual = null; // Add this new property
      this.retry_options_visible = {};
      this.connection_error = false;

      // Restore batch type
      this.batch_type = currentBatchType;

      // Reset file input
      if (this.$refs.batchFileInput) {
        this.$refs.batchFileInput.value = '';
      }
    },

    reset_file_context(options = {}) {
      const { preserveFile = false } = options;
      if (!preserveFile) {
        this.batch_file = null;
      }
      this.detected_columns = [];
      this.company_name_column = '';
      this.registration_number_column = '';
      this.id_number_column = '';
      this.first_name_column = '';
      this.last_name_column = '';
      this.extracted_records = [];
    },

    handle_file_upload(event) {
      const file = event.target.files ? event.target.files[0] : null;
      if (!file) return;

      this.reset_file_context({ preserveFile: true });
      this.batch_error = '';
      this.batch_success = '';
      this.connection_error = false;

      const allowed_types = ['.csv', '.xlsx', '.xls'];
      const file_extension = '.' + file.name.split('.').pop().toLowerCase();

      if (!allowed_types.includes(file_extension)) {
        this.show_error('Please select a valid file format (CSV or Excel)');
        return;
      }

      if (file.size > 20 * 1024 * 1024) {
        this.show_error('File size must be less than 20MB');
        return;
      }

      this.batch_file = file;
      this.file_processing = true;
      this.detect_columns();
    },

    show_error(message, duration = 15000) {
      this.batch_error = message;
      this.last_error_timestamp = new Date().toLocaleTimeString();
      
      // Clear any existing timeout
      if (this.error_display_timeout) {
        clearTimeout(this.error_display_timeout);
      }
      
      // Auto-clear error after specified duration
      this.error_display_timeout = setTimeout(() => { 
        this.batch_error = ''; 
        this.last_error_timestamp = null;
      }, duration);
    },

    show_success(message, duration = 10000) {
      this.batch_success = message;
      setTimeout(() => { this.batch_success = ''; }, duration);
    },

    add_api_error(error) {
      const errorObj = {
        id: Date.now() + Math.random(),
        message: error.message || error,
        details: error.details || null,
        timestamp: new Date().toLocaleTimeString(),
        type: error.type || 'error'
      };
      
      this.api_errors.unshift(errorObj);
      
      // Keep only last 5 errors to prevent memory issues
      if (this.api_errors.length > 5) {
        this.api_errors = this.api_errors.slice(0, 5);
      }
    },

    clear_api_error(errorId) {
      this.api_errors = this.api_errors.filter(error => error.id !== errorId);
    },

    clear_all_errors() {
      this.api_errors = [];
      this.batch_error = '';
      this.connection_error = false;
      if (this.error_display_timeout) {
        clearTimeout(this.error_display_timeout);
      }
    },

    remove_file() {
      this.reset_file_context();
      if (this.$refs.batchFileInput) {
        try { this.$refs.batchFileInput.value = ''; } catch (e) { /* ignore */ }
      }
    },

    // Optimized column detection with Web Workers simulation
    async detect_columns() {
      if (!this.batch_file) return;

      try {
        this.file_processing = true;
        const data = await this.read_file_async(this.batch_file);
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellText: true,     // Preserve text formatting
          raw: false,         // Don't parse numbers automatically
          cellDates: false,   // Don't convert dates automatically
          cellNF: false,      // Don't apply number format
          cellStyles: false   // Don't read cell styles
        });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Z1');
        range.e.r = Math.min(range.e.r, 2);
        
        const firstRowData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1, 
          range: XLSX.utils.encode_range(range)
        });

        if (!firstRowData || firstRowData.length === 0 || !firstRowData[0]) {
          this.show_error('No data found in the file');
          return;
        }
        
        const firstRow = firstRowData[0];
        
        this.detected_columns = firstRow
          .map((cell, index) => {
            const cellValue = cell ? String(cell).trim() : '';
            return cellValue || `Column ${index + 1}`;
          })
          .filter(col => col && col.length > 0);

        if (this.detected_columns.length === 0) {
          this.show_error('No valid columns detected in the file');
          return;
        }
        
        this.auto_detect_columns();

        const missingCompanyColumns = this.batch_type === 'company' && (!this.company_name_column || !this.registration_number_column);
        const missingIndividualColumns = this.batch_type === 'individual' && (!this.id_number_column || !this.first_name_column || !this.last_name_column);

        if (missingCompanyColumns || missingIndividualColumns) {
          const message = this.batch_type === 'company'
            ? 'Could not detect the required Company Name and Registration Number columns. Please map them manually.'
            : 'Could not detect the required ID Number, First Name, and Last Name columns. Please map them manually.';
          this.show_error(message, 8000);
        }
        await this.extract_records();
      } catch (error) {
        this.show_error('Error reading file: ' + (error.message || error));
        console.error('File parsing error:', error);
      } finally {
        this.file_processing = false;
      }
    },

    // Async file reading
    read_file_async(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(new Uint8Array(e.target.result));
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },

    auto_detect_columns() {
      if (this.batch_type === 'company') {
        const company_keywords = ['company', 'name', 'companies', 'organization', 'organisation', 'names'];
        const registration_keywords = ['registration', 'reg', 'number', 'id', 'no.', 'regnum', 'registrations'];

        let detectedCompany = '';
        let detectedReg = '';

        for (const column of this.detected_columns) {
          const lower_col = String(column || '').toLowerCase();
          if (!detectedCompany && company_keywords.some(kw => lower_col.includes(kw))) {
            detectedCompany = column;
          }
          if (!detectedReg && registration_keywords.some(kw => lower_col.includes(kw))) {
            detectedReg = column;
          }
          if (detectedCompany && detectedReg) break;
        }

        this.company_name_column = detectedCompany;
        this.registration_number_column = detectedReg;
      } else {
        const id_keywords = ['id', 'identity', 'idnumber', 'id_number', 'idno', 'passport', 'document'];
        const first_name_keywords = ['first', 'firstname', 'fname', 'first_name', 'given', 'forename'];
        const last_name_keywords = ['last', 'lastname', 'lname', 'last_name', 'surname', 'family'];

        let detectedId = '';
        let detectedFirst = '';
        let detectedLast = '';

        for (const column of this.detected_columns) {
          const lower_col = String(column || '').toLowerCase();
          if (!detectedId && id_keywords.some(kw => lower_col.includes(kw))) {
            detectedId = column;
          }
          if (!detectedFirst && first_name_keywords.some(kw => lower_col.includes(kw))) {
            detectedFirst = column;
          }
          if (!detectedLast && last_name_keywords.some(kw => lower_col.includes(kw))) {
            detectedLast = column;
          }
          if (detectedId && detectedFirst && detectedLast) break;
        }

        this.id_number_column = detectedId;
        this.first_name_column = detectedFirst;
        this.last_name_column = detectedLast;
      }
    },

    // Validation functions for individuals
    validate_id_number(idNumber) {
      if (!idNumber || typeof idNumber !== 'string') {
        return { valid: false, error: 'ID number is required' };
      }

      const cleaned = idNumber.trim().replace(/\s+/g, '');

      if (cleaned.length !== 13) {
        return { valid: false, error: 'ID number must be exactly 13 digits' };
      }

      if (!/^\d{13}$/.test(cleaned)) {
        return { valid: false, error: 'ID number must contain only digits' };
      }

      // Use the exact same validation logic as validate_id function from validations.js
      const isValid = validate_id(cleaned);
      if (!isValid) {
        return { valid: false, error: 'Invalid format. Must be a valid South African ID number' };
      }

      return { valid: true, cleaned: cleaned };
    },

    validate_name_field(name, fieldName) {
      if (!name || typeof name !== 'string') {
        return { valid: false, error: `${fieldName} is required` };
      }

      const cleaned = name.trim();
      
      if (cleaned.length === 0) {
        return { valid: false, error: `${fieldName} cannot be empty` };
      }

      if (cleaned.length < 2) {
        return { valid: false, error: `${fieldName} must be at least 2 characters` };
      }

      if (cleaned.length > 50) {
        return { valid: false, error: `${fieldName} must be less than 50 characters` };
      }

      // Check for valid characters (letters, spaces, hyphens, apostrophes)
      if (!/^[a-zA-Z\s'-]+$/.test(cleaned)) {
        return { valid: false, error: `${fieldName} can only contain letters, spaces, hyphens and apostrophes` };
      }

      return { valid: true, cleaned: cleaned };
    },

    show_validation_summary() {
      const total = this.extracted_records.length;
      const valid = this.valid_records_count;
      const invalid = this.invalid_records_count;
      const missing = total - valid - invalid;

      if (invalid > 0 || missing > 0) {
        let message;
        
        if (this.batch_type === 'company') {
          message = `Registration Number Validation Summary:\n`;
          message += `• Total companies: ${total}\n`;
          message += `• Valid format: ${valid}\n`;
          if (invalid > 0) {
            message += `• Invalid format: ${invalid} (must be YYYY/NNNNNN/NN)\n`;
          }
          if (missing > 0) {
            message += `• Missing registration: ${missing}\n`;
          }
          message += `\nOnly companies with valid registration numbers will be processed.`;
        } else {
          message = `Individual Data Validation Summary:\n`;
          message += `• Total individuals: ${total}\n`;
          message += `• Valid records: ${valid}\n`;
          if (invalid > 0) {
            message += `• Invalid records: ${invalid}\n`;
          }
          if (missing > 0) {
            message += `• Incomplete records: ${missing}\n`;
          }
          message += `\nOnly individuals with valid ID numbers, first names, and last names will be processed.`;
        }

        this.show_error(message);
      }
    },

    show_registration_validation_summary() {
      // Backward compatibility - redirect to new method
      this.show_validation_summary();
    },

    // Unified record extraction for both companies and individuals
    async extract_records() {
      if (this.batch_type === 'company') {
        return await this.extract_companies();
      } else {
        return await this.extract_individuals();
      }
    },

    // Optimized company extraction with chunking
    async extract_companies() {
      if (!this.batch_file || !this.company_name_column || !this.registration_number_column) {
        this.extracted_records = [];
        return;
      }

      try {
        const data = await this.read_file_async(this.batch_file);
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellText: true,     // Preserve text formatting
          raw: false,         // Don't parse numbers automatically
          cellDates: false,   // Don't convert dates automatically
          cellNF: false,      // Don't apply number format
          cellStyles: false   // Don't read cell styles
        });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (!rawData || rawData.length === 0) {
          this.show_error('No data found in the file');
          this.extracted_records = [];
          return;
        }

        let headerRow = [];
        let dataRows = [];

        if (this.has_header_row) {
          headerRow = (rawData[0] || []).map(cell => cell ? String(cell).trim() : '');
          dataRows = rawData.slice(1);
        } else {
          headerRow = this.detected_columns;
          dataRows = rawData;
        }

        const companyNameIndex = headerRow.indexOf(this.company_name_column);
        const registrationNumberIndex = headerRow.indexOf(this.registration_number_column);

        if (companyNameIndex === -1 || registrationNumberIndex === -1) {
          this.show_error('Selected columns not found in the file');
          this.extracted_records = [];
          return;
        }

      // Process all companies first
      const allCompanies = await this.process_companies_chunked(
        dataRows,
        companyNameIndex,
        registrationNumberIndex
      );

      // Remove duplicates by registration number
      const uniqueCompaniesMap = new Map();
      for (const company of allCompanies) {
        if (company.registration_number) {
          // Use normalized registration number as key to catch similar entries
          const normalizedReg = this.normalize_registration_number(company.registration_number);
          if (!uniqueCompaniesMap.has(normalizedReg)) {
            uniqueCompaniesMap.set(normalizedReg, company);
          }
        } else {
          // If no registration number, include it (it will be marked invalid)
          uniqueCompaniesMap.set(Math.random().toString(36).substring(7), company);
        }
      }

      this.extracted_records = Array.from(uniqueCompaniesMap.values());

      // Show validation summary
      this.$nextTick(() => {
        this.show_validation_summary();
      });

      } catch (error) {
        this.show_error('Error extracting data: ' + (error.message || error));
        console.error('Data extraction error:', error);
        this.extracted_records = [];
      }
    },

    // Extract individuals with validation
    async extract_individuals() {
      if (!this.batch_file || !this.id_number_column || !this.first_name_column || !this.last_name_column) {
        this.extracted_records = [];
        return;
      }

      try {
        const data = await this.read_file_async(this.batch_file);
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellText: true,     // Preserve text formatting
          raw: false,         // Don't parse numbers automatically
          cellDates: false,   // Don't convert dates automatically
          cellNF: false,      // Don't apply number format
          cellStyles: false   // Don't read cell styles
        });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (!rawData || rawData.length === 0) {
          this.show_error('No data found in the file');
          this.extracted_records = [];
          return;
        }

        let headerRow = [];
        let dataRows = [];

        if (this.has_header_row) {
          headerRow = (rawData[0] || []).map(cell => cell ? String(cell).trim() : '');
          dataRows = rawData.slice(1);
        } else {
          headerRow = this.detected_columns;
          dataRows = rawData;
        }

        const idNumberIndex = headerRow.indexOf(this.id_number_column);
        const firstNameIndex = headerRow.indexOf(this.first_name_column);
        const lastNameIndex = headerRow.indexOf(this.last_name_column);

        if (idNumberIndex === -1 || firstNameIndex === -1 || lastNameIndex === -1) {
          this.show_error('Selected columns not found in the file');
          this.extracted_records = [];
          return;
        }

        // Process all individuals
        const allIndividuals = await this.process_individuals_chunked(
          dataRows,
          idNumberIndex,
          firstNameIndex,
          lastNameIndex
        );

        // Remove duplicates by ID number
        const uniqueIndividualsMap = new Map();
        for (const individual of allIndividuals) {
          if (individual.id_number) {
            if (!uniqueIndividualsMap.has(individual.id_number)) {
              uniqueIndividualsMap.set(individual.id_number, individual);
            }
          } else {
            // If no ID number, include it (it will be marked invalid)
            uniqueIndividualsMap.set(Math.random().toString(36).substring(7), individual);
          }
        }

        this.extracted_records = Array.from(uniqueIndividualsMap.values());

        // Show validation summary
        this.$nextTick(() => {
          this.show_validation_summary();
        });

      } catch (error) {
        this.show_error('Error extracting data: ' + (error.message || error));
        console.error('Data extraction error:', error);
        this.extracted_records = [];
      }
    },

    validate_registration_number(regNumber) {
      if (!regNumber || typeof regNumber !== 'string') {
        return { valid: false, error: 'Registration number is required' };
      }

      const cleaned = regNumber.trim();

      const regexPattern = /^\d{4}\/\d{6}\/\d{2}$/;

      if (!regexPattern.test(cleaned)) {
        return {
          valid: false,
          error: 'Invalid format. Must be YYYY/NNNNNN/NN (e.g., 1998/120552/07)'
        };
      }

      return { valid: true, cleaned: cleaned };
    },
    // Process companies in chunks to avoid blocking UI
    async process_companies_chunked(dataRows, companyNameIndex, registrationNumberIndex) {
      const companies = [];
      const chunkSize = 50;
      
      const cleanText = (text) => {
        if (!text) return '';
        return String(text)
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/[\r\n\t]/g, '')
          .trim();
      };

      const cleanRegistrationNumber = (regNum) => {
        if (!regNum) return '';
        return String(regNum)
          .replace(/\s+/g, '')
          .trim();
      };

      for (let i = 0; i < dataRows.length; i += chunkSize) {
        const chunk = dataRows.slice(i, i + chunkSize);
        
        const chunkCompanies = chunk
          .filter(row => row && Array.isArray(row) && row.length > 0)
          .map((row, index) => {
            const companyName = cleanText(row[companyNameIndex]);
            let registrationNumber = cleanText(row[registrationNumberIndex]);
            registrationNumber = cleanRegistrationNumber(registrationNumber);
            
            const regValidation = this.validate_registration_number(registrationNumber);

            const company = {
              name: companyName,
              registration_number: registrationNumber,
              row_index: i + index + (this.has_header_row ? 2 : 1)
            };

            company._validation = {
              registration_valid: regValidation.valid,
              validation_error: regValidation.error || null
            };

            return company;
          })
          .filter(company => company.name || company.registration_number);
        
        companies.push(...chunkCompanies);
        
        // Allow UI to update between chunks
        if (i + chunkSize < dataRows.length) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      return companies;
    },

    // Process individuals in chunks to avoid blocking UI
    async process_individuals_chunked(dataRows, idNumberIndex, firstNameIndex, lastNameIndex) {
      const individuals = [];
      const chunkSize = 50;
      
      const cleanText = (text) => {
        if (!text) return '';
        return String(text)
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/[\r\n\t]/g, '')
          .trim();
      };

      const cleanIdNumber = (idNum) => {
        if (!idNum) return '';
        
        // Convert to string and clean
        let cleaned = String(idNum)
          .replace(/\s+/g, '')
          .replace(/[^\d]/g, '') // Remove any non-digit characters
          .trim();
        
        if (idNum.toString().includes('e+') || idNum.toString().includes('E+')) {
          // Convert scientific notation to regular number
          const num = parseFloat(idNum);
          if (!isNaN(num)) {
            cleaned = Math.floor(num).toString();
          }
        }
        
        // If it's all digits and less than 13 characters, it might have lost leading zeros
        if (/^\d+$/.test(cleaned) && cleaned.length < 13 && cleaned.length >= 12) {
          // Pad with leading zeros to make it 13 digits
          cleaned = cleaned.padStart(13, '0');
        }
        
        return cleaned;
      };

      for (let i = 0; i < dataRows.length; i += chunkSize) {
        const chunk = dataRows.slice(i, i + chunkSize);
        
        const chunkIndividuals = chunk
          .filter(row => row && Array.isArray(row) && row.length > 0)
          .map((row, index) => {
            const idNumber = cleanIdNumber(row[idNumberIndex]);
            const firstName = cleanText(row[firstNameIndex]);
            const lastName = cleanText(row[lastNameIndex]);
            
            const idValidation = this.validate_id_number(idNumber);
            const firstNameValidation = this.validate_name_field(firstName, 'First name');
            const lastNameValidation = this.validate_name_field(lastName, 'Last name');

            const individual = {
              id_number: idNumber,
              first_name: firstName,
              last_name: lastName,
              row_index: i + index + (this.has_header_row ? 2 : 1)
            };

            const allValid = idValidation.valid && firstNameValidation.valid && lastNameValidation.valid;
            
            individual._validation = {
              id_valid: idValidation.valid,
              id_error: idValidation.error || null,
              first_name_valid: firstNameValidation.valid,
              first_name_error: firstNameValidation.error || null,
              last_name_valid: lastNameValidation.valid,
              last_name_error: lastNameValidation.error || null,
              all_valid: allValid
            };

            return individual;
          })
          .filter(individual => individual.id_number || individual.first_name || individual.last_name);
        
        individuals.push(...chunkIndividuals);
        
        // Allow UI to update between chunks
        if (i + chunkSize < dataRows.length) {
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      return individuals;
    },

    remove_duplicate_records() {
      const seen = new Set();
      const uniqueRecords = [];
      
      for (const record of this.extracted_records) {
        let keyField;
        
        if (this.batch_type === 'company') {
          keyField = record.registration_number;
          if (!keyField) {
            uniqueRecords.push(record);
            continue;
          }
          keyField = keyField.trim().toUpperCase();
        } else {
          keyField = record.id_number;
          if (!keyField) {
            uniqueRecords.push(record);
            continue;
          }
          keyField = keyField.trim();
        }
        
        if (!seen.has(keyField)) {
          seen.add(keyField);
          uniqueRecords.push(record);
        }
      }
      
      this.extracted_records = uniqueRecords;
    },

    async process_batch_upload() {
      if (!this.can_start_processing) {
        this.show_error(`Please configure all required columns and ensure valid ${this.batch_type === 'company' ? 'companies' : 'individuals'} exist`);
        return;
      }

      let valid_records;
      if (this.batch_type === 'company') {
        valid_records = this.extracted_records.filter(
          c => (c.name || '').trim() && (c.registration_number || '').trim() &&
               c._validation && c._validation.registration_valid === true
        );
        
        if (valid_records.length === 0) {
          this.show_error('No companies with valid registration numbers found');
          return;
        }
      } else {
        valid_records = this.extracted_records.filter(
          i => (i.id_number || '').trim() && (i.first_name || '').trim() && (i.last_name || '').trim() &&
               i._validation && i._validation.all_valid === true
        );
        
        
        if (valid_records.length === 0) {
          this.show_error('No individuals with valid ID numbers, first names, and last names found');
          return;
        }
      }

      await this.send_batch_to_api(valid_records);
    },

    async send_batch_to_api(records) {
      const TOKEN = localStorage.getItem('token');
      
      let API_URL, payload;
      
      if (this.batch_type === 'company') {
        API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_upload_companies/`;
        
        const valid_companies = records
          .filter(c =>
            (c.name || '').trim() &&
            (c.registration_number || '').trim() &&
            c._validation && c._validation.registration_valid === true
          )
          .map(c => ({
            name: c.name,
            registration_number: c.registration_number,
            row_index: c.row_index
          }));

        if (valid_companies.length === 0) {
          this.show_error('No companies with valid registration numbers found');
          return;
        }

        payload = {
          workflow_id: this.current_batch_workflow_id,
          companies: valid_companies
        };
      } else {
        // Individual verification API
        API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_upload_individuals/`;
        
        const valid_individuals = records
          .filter(i =>
            (i.id_number || '').trim() &&
            (i.first_name || '').trim() &&
            (i.last_name || '').trim() &&
            i._validation && i._validation.all_valid === true
          )
          .map(i => ({
            id_number: i.id_number,
            first_name: i.first_name,
            last_name: i.last_name,
            row_index: i.row_index
          }));

        if (valid_individuals.length === 0) {
          this.show_error('No individuals with valid data found');
          return;
        }

        payload = {
          workflow_id: this.current_batch_workflow_id,
          individuals: valid_individuals
        };
      }

      this.batch_processing = true;
      this.clear_all_errors();

      try {
        this.add_api_error({
          message: `Starting batch processing for ${records.length} ${this.batch_type === 'company' ? 'companies' : 'individuals'}...`,
          type: 'info'
        });

        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${TOKEN}`
          },
          body: JSON.stringify(payload)
        });

        let result;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          throw new Error(`Server returned ${response.status}: ${response.statusText}. Expected JSON response.`);
        }

        if (response.ok && result.status === 'success') {
          this.batch_job_id = result.batch_job_id;
          const recordType = this.batch_type === 'company' ? 'companies' : 'individuals';
          const totalCount = this.batch_type === 'company' ? result.total_companies : result.total_individuals;
          this.show_success(`Batch processing started successfully for ${totalCount || records.length} ${recordType}`);
          
          this.start_status_polling();
        } else {
          // Handle API error response
          const errorMessage = result.message || result.error || `HTTP ${response.status}: ${response.statusText}`;
          const errorDetails = result.errors || result.details || null;
          
          this.add_api_error({
            message: 'Failed to start batch processing',
            details: errorDetails ? JSON.stringify(errorDetails) : errorMessage,
            type: 'error'
          });
          
          this.show_error(errorMessage);
        }

      } catch (error) {
        console.error('Batch upload error:', error);
        
        const errorMessage = this.format_error_message(error);
        
        this.add_api_error({
          message: 'Network error during batch upload',
          details: errorMessage,
          type: 'error'
        });
        
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
          this.connection_error = true;
          this.show_error('Connection error. Please check your internet connection and try again.');
        } else {
          this.show_error(`Failed to start batch processing: ${errorMessage}`);
        }
      } finally {
        this.batch_processing = false;
      }
    },


    ensure_polling_active() {
      // If polling is not active but we have a batch job, restart it
      if (!this.polling_interval && this.batch_job_id) {
        this.start_status_polling();
      }
    },

    updateCompanyReactive(index, updates) {
      this.batch_companies[index] = { ...this.batch_companies[index], ...updates };
    },

    start_status_polling() {
      // Clear any existing interval first
      if (this.polling_interval) {
        clearInterval(this.polling_interval);
        this.polling_interval = null;
      }
      
      // Fetch status immediately
      this.fetch_batch_job_status();
      
      // Set up new interval
      this.polling_interval = setInterval(() => {
        if (this.batch_job_id && !this.fetching_status) {
          this.fetch_batch_job_status();
        }
      }, 3000);
    },

    async fetch_batch_job_status() {
      if (!this.batch_job_id) {
        this.stop_polling();
        return;
      }
      
      if (this.fetching_status) {
        return;
      }
      
      const TOKEN = localStorage.getItem('token');
      const API_URL = `${API_BASE_URL}/verify_api/batch_api/batch_job_status/${this.batch_job_id}/`;
      
      this.fetching_status = true;

      try {
        const response = await fetch(API_URL, {
          method: 'GET',
          headers: {
            'Authorization': `Token ${TOKEN}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.status === 'error') {
          throw new Error(result.message || 'API returned error status');
        }
        
        this.connection_error = false;
        this.batch_job_data = { ...result };
        this.last_updated_time = new Date().toLocaleTimeString();
        
        // Update records array based on batch type
        if (result.batch_type === 'company' && Array.isArray(result.companies)) {
          const existingCompaniesMap = new Map();
          this.batch_companies.forEach(company => {
            const key = `${company.company_name}||${company.registration_number}`;
            existingCompaniesMap.set(key, company);
          });
          
          this.batch_companies = result.companies.map(company => {
            const key = `${company.company_name}||${company.registration_number}`;
            const existingCompany = existingCompaniesMap.get(key);
            
            const total = company.total_services || 1;
            const completed = company.completed_services || 0;
            const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
            
            return {
              ...company,
              progress_percentage: progress,
              ...(existingCompany && existingCompany._ui_state ? { _ui_state: existingCompany._ui_state } : {})
            };
          });
        } else if (result.batch_type === 'individual' && Array.isArray(result.individuals)) {
          const existingIndividualsMap = new Map();
          this.batch_individuals.forEach(individual => {
            const key = `${individual.first_name}||${individual.last_name}||${individual.id_number}`;
            existingIndividualsMap.set(key, individual);
          });
          
          this.batch_individuals = result.individuals.map(individual => {
            const key = `${individual.first_name}||${individual.last_name}||${individual.id_number}`;
            const existingIndividual = existingIndividualsMap.get(key);
            
            const total = individual.total_services || 1;
            const completed = individual.completed_services || 0;
            const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
            
            return {
              ...individual,
              progress_percentage: progress,
              ...(existingIndividual && existingIndividual._ui_state ? { _ui_state: existingIndividual._ui_state } : {})
            };
          });
        } else {
          this.batch_companies = [];
          this.batch_individuals = [];
          this.add_api_error({
            message: 'Invalid records data received from server',
            details: 'Expected array but got: ' + typeof (result.companies || result.individuals),
            type: 'warning'
          });
        }
        
        // Handle batch completion
        const hasProcessingRecords = this.batch_type === 'company'
          ? this.batch_companies.some(c => c.status === 'processing' || c.status === 'pending')
          : this.batch_individuals.some(i => i.status === 'processing' || i.status === 'pending');
        
        if ((result.status === 'completed' || result.status === 'failed') && !hasProcessingRecords) {
          this.stop_polling();
          
          if (result.status === 'completed') {
            const recordType = this.batch_type === 'company' ? 'companies' : 'individuals';
            const successCount = this.successful_count;
            const failedCount = this.failed_count;
            this.show_success(
              `Batch processing completed! ${successCount} ${recordType} succeeded, ${failedCount} failed`
            );
          } else if (result.status === 'failed') {
            this.add_api_error({
              message: 'Batch processing failed',
              details: result.error_message || 'Unknown batch failure',
              type: 'error'
            });
          }
        } else {
          this.ensure_polling_active();
        }

      } catch (error) {
        console.error('[FRONTEND STATUS] Error fetching batch status:', error);
        
        const errorMessage = this.format_error_message(error);
        
        // Mark connection error for network issues
        if (error.name === 'TypeError' || error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
          this.connection_error = true;
          this.add_api_error({
            message: 'Connection error while fetching status',
            details: 'Check your internet connection and try refreshing',
            type: 'warning'
          });
        } else {
          this.add_api_error({
            message: 'Failed to fetch batch status',
            details: errorMessage,
            type: 'error'
          });
        }
      } finally {
        this.fetching_status = false;
      }
    },


    show_retry_options(companyIndex) {
      this.retry_options_visible[companyIndex] = true;
    },

    // Hide retry options for a company
    hide_retry_options(companyIndex) {
      this.retry_options_visible[companyIndex] = false;
    },

    // Check if retry options are visible for a company
    are_retry_options_visible(companyIndex) {
      return this.retry_options_visible[companyIndex] || false;
    },

    // Get detailed error message for display
    get_detailed_error_message(company) {
      if (!company.error_message) return 'No error details available';
      let message = company.error_message;
      if (company.failed_service) {
        message = `Failed at: ${company.failed_service}. ${message}`;
      }
      
      return message;
    },

        get_retry_button_text(company) {
      if (!company.failed_service) return 'Retry';
      return `Retry from ${company.failed_service}`;
    },

    // Manual refresh with user feedback
    async manual_refresh() {
      this.add_api_error({
        message: 'Manually refreshing batch status...',
        type: 'info'
      });
      
      await this.fetch_batch_job_status();
      
      if (!this.connection_error && !this.has_active_errors) {
        this.show_success('Status refreshed successfully');
      }
    },

    format_error_message(error) {
      if (typeof error === 'string') {
        return error;
      } else if (error.message) {
        return error.message;
      } else if (error.statusText) {
        return error.statusText;
      } else {
        return 'Unknown error occurred';
      }
    },

    format_file_size(bytes) {
      if (typeof bytes !== 'number' || bytes < 0) return '0 Bytes';
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }
  },

  beforeUnmount() {
    if (this.polling_interval) {
      clearInterval(this.polling_interval);
      this.polling_interval = null;
    }
    if (this.error_display_timeout) {
      clearTimeout(this.error_display_timeout);
    }
  }
};
</script>

<style scoped>
.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  padding: 1rem 1.25rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

/* Upload Section */
.upload-section .border-dashed {
  border: 2px dashed #ccc;
  transition: all 0.2s ease-in-out;
}
.upload-section .border-dashed:hover {
  border-color: #0d6efd;
  background-color: #f0f8ff;
}
.upload-section p {
  margin-bottom: 0.4rem;
}
.upload-section button {
  transition: all 0.2s ease-in-out;
}
.upload-section button:hover {
  transform: translateY(-1px);
}

/* File preview */
.text-truncate {
  max-width: 250px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Table containers */
.companies-preview-container,
.processing-table-container {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
}
.table {
  margin-bottom: 0;
}
.table th {
  font-weight: 600;
  background: #f8f9fa;
}
.table-hover tbody tr:hover {
  background-color: #f5faff;
}

/* Sticky headers for long lists */
.table thead.sticky-top {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  z-index: 1;
}

/* Progress Bars */
.progress {
  background-color: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}
.progress-bar {
  font-size: 0.7rem;
  font-weight: 500;

  background-color: var(--verify-green);
  width: 7px;
  height: 100%;
  position: inherit;
  left: 20px;
}

/* Status badges */
.badge {
  padding: 0.4em 0.6em;
  font-weight: 500;
  border-radius: 6px;
}

.error-width{
  max-width: 200px;
}

.progress-width{
  width: 60px; height: 8px;
}

.batch-width{
  max-width: 250px
}

.border-height{
  max-height: 300px;
}

.progress-height{
  height: 12px;
}

.company-width{
  max-width: 150px
}

.table-height{
  max-height: 400px;
}

/* Responsive tweaks */
@media (max-width: 768px) {
  .modal-dialog {
    margin: 1rem;
  }
  .table th, .table td {
    font-size: 0.75rem;
  }
  .upload-section .border-dashed {
    padding: 1.5rem 1rem;
  }
}
</style>
